import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography, Box, Grid, Divider, 
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Tab, Tabs,
  FormControl, InputLabel, Select, MenuItem, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { 
  BarChart, 
  PieChart,
  pieArcLabelClasses
} from '@mui/x-charts';
import analyticsApi from '../../../services/analyticsapi';

/**
 * Modal component to display detailed sales breakdown and analysis
 */
const SalesBreakdown = ({ open, onClose, data, fullScreen = false }) => {
  const [tabValue, setTabValue] = useState(0);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Generate a list of years (current year and 4 previous years)
  const years = Array.from({length: 6}, (_, i) => new Date().getFullYear() - i);
  
  useEffect(() => {
    // Initialize with the data passed from parent
    if (data) {
      // Ensure the data has all required fields, set defaults if missing
      const processedData = {
        ...data,
        // Add default values for new fields if they're not present
        total_returns_amount: data.total_returns_amount || 0,
        total_returns_units: data.total_returns_units || 0,
        total_purchase_amount: data.total_purchase_amount || 0,
        total_purchase_units: data.total_purchase_units || 0,
        net_amount: data.net_amount || (data.total_amount || 0)
      };
      
      setSalesData(processedData);
      
      // Parse the period (e.g., "July 2025") to set initial month and year
      const periodParts = data.period.split(' ');
      if (periodParts.length === 2) {
        setMonth(periodParts[0]);
        setYear(parseInt(periodParts[1]));
      }
    }
  }, [data]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleMonthChange = (event) => {
    setMonth(event.target.value);
  };
  
  const handleYearChange = (event) => {
    setYear(event.target.value);
  };
  
  const fetchSalesData = async () => {
    if (!month || !year) return;
    
    try {
      setLoading(true);
      const period = `${month} ${year}`;
      const response = await analyticsApi.viewSalesBreakdown(period);
      setSalesData(response.data);
    } catch (err) {
      console.error("Error fetching sales data:", err);
    } finally {
      setLoading(false);
    }
  };
  
  if (!salesData) {
    return null;
  }

  // Custom colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#FF6B6B'];
  
  // Format marketplace data for chart
  const marketplaceChartData = salesData.marketplace_breakdown?.map(item => ({
    name: item.marketplace,
    value: item.total_amount,
    percentage: item.percentage
  })) || [];
  
  // Format category data for chart
  const categoryChartData = salesData.category_breakdown?.map(item => ({
    name: item.category,
    value: item.total_amount,
    percentage: item.percentage
  })) || [];
  
  // Format sales vs returns data for chart
  const salesVsReturnsData = [
    { id: 'Net Sales', value: salesData.net_amount || 0, label: 'Net Sales', color: '#4caf50' },
    { id: 'Returns', value: salesData.total_returns_amount || 0, label: 'Returns', color: '#f44336' }
  ];
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Sales Breakdown: {salesData.period}
        </Typography>
        <Button 
          onClick={onClose} 
          color="inherit" 
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <Box sx={{ display: 'flex', px: 3, py: 2, alignItems: 'center', gap: 2 }}>
        <FormControl sx={{ width: 150 }}>
          <InputLabel>Month</InputLabel>
          <Select
            value={month}
            label="Month"
            onChange={handleMonthChange}
            size="small"
          >
            {months.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl sx={{ width: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={handleYearChange}
            size="small"
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button 
          variant="contained" 
          onClick={fetchSalesData}
          disabled={loading}
          size="small"
        >
          {loading ? <CircularProgress size={24} /> : 'Update'}
        </Button>
      </Box>
      
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Overview" />
        <Tab label="Marketplace Analysis" />
        <Tab label="Category Analysis" />
        <Tab label="Top Products" />
      </Tabs>
      
      <DialogContent dividers>
        {/* Overview Tab */}
        {tabValue === 0 && (
          <Box>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" color="text.secondary">Gross Sales</Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    ₹ {salesData.total_amount?.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For {salesData.period}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: salesData.total_returns_amount > 0 ? 'rgba(255, 0, 0, 0.05)' : 'transparent' }}>
                  <Typography variant="subtitle1" color="text.secondary">Returns</Typography>
                  <Typography variant="h4" sx={{ my: 1, color: salesData.total_returns_amount > 0 ? 'error.main' : 'text.primary' }}>
                    ₹ {salesData.total_returns_amount?.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {salesData.total_returns_units?.toLocaleString('en-IN')} Units
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(0, 128, 0, 0.05)' }}>
                  <Typography variant="subtitle1" color="text.secondary">Net Sales</Typography>
                  <Typography variant="h4" sx={{ my: 1, color: 'success.main' }}>
                    ₹ {salesData.net_amount?.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    After Returns
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" color="text.secondary">Total Units Sold</Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {salesData.total_units?.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Net: {(salesData.total_units - (salesData.total_returns_units || 0))?.toLocaleString('en-IN')}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" color="text.secondary">Avg. Order Value</Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    ₹ {salesData.total_units ? Math.round(salesData.total_amount / salesData.total_units).toLocaleString('en-IN') : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Per Unit
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            
            {/* Purchase Data Section */}
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Purchase Summary</Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: 'rgba(25, 118, 210, 0.05)' }}>
                  <Typography variant="subtitle1" color="text.secondary">Total Purchases</Typography>
                  <Typography variant="h4" sx={{ my: 1, color: 'primary.main' }}>
                    ₹ {salesData.total_purchase_amount?.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For {salesData.period}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" color="text.secondary">Units Purchased</Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {salesData.total_purchase_units?.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inventory Added
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" color="text.secondary">Avg. Purchase Cost</Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    ₹ {salesData.total_purchase_units ? Math.round(salesData.total_purchase_amount / salesData.total_purchase_units).toLocaleString('en-IN') : 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Per Unit
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            
            
            
            {/* Returns Statistics Section */}
            {salesData.total_returns_amount > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Returns Analysis</Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(255, 0, 0, 0.02)' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">Return Rate</Typography>
                        <Typography variant="h5" color="error.main">
                          {salesData.total_units ? (((salesData.total_returns_units || 0) / salesData.total_units) * 100).toFixed(2) : 0}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">Units Returned</Typography>
                        <Typography variant="h5" color="text.primary">
                          {salesData.total_returns_units?.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">Return Amount</Typography>
                        <Typography variant="h5" color="text.primary">
                          ₹ {salesData.total_returns_amount?.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">Impact</Typography>
                        <Typography variant="h5" color="text.primary">
                          {salesData.total_amount ? ((salesData.total_returns_amount / salesData.total_amount) * 100).toFixed(2) : 0}% of Gross
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Marketplace Distribution</Typography>
                <Box sx={{ height: 300, width: '100%' }}>
                  <PieChart
                    series={[
                      {
                        data: marketplaceChartData.map(item => ({
                          id: item.name,
                          value: item.value,
                          label: item.name,
                          color: COLORS[marketplaceChartData.findIndex(d => d.name === item.name) % COLORS.length]
                        })),
                        innerRadius: 30,
                        outerRadius: 100,
                        paddingAngle: 2,
                        cornerRadius: 4,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        valueFormatter: (item) => `₹ ${item.value.toLocaleString('en-IN')}`,
                      },
                    ]}
                    height={300}
                    slotProps={{
                      legend: {
                        direction: 'row',
                        position: { vertical: 'bottom', horizontal: 'middle' },
                        padding: 0,
                      },
                    }}
                    sx={{
                      [`& .${pieArcLabelClasses.root}`]: {
                        display: 'none',
                      },
                    }}
                  />
                </Box>
              </Grid>

            </Grid>
          </Box>
        )}
        
        {/* Marketplace Analysis Tab */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Marketplace Analysis</Typography>
            <Box sx={{ height: 400, mb: 3, width: '100%' }}>
              <BarChart
                dataset={salesData.marketplace_breakdown?.map(item => ({
                  marketplace: item.marketplace,
                  total_units: item.total_units,
                  total_amount: item.total_amount / 1000, // Scale down to make comparable
                  original_amount: item.total_amount // Keep original for tooltip
                })) || []}
                xAxis={[{ scaleType: 'band', dataKey: 'marketplace', label: 'Marketplace' }]}
                series={[
                  { 
                    dataKey: 'total_units', 
                    label: 'Units Sold',
                    valueFormatter: (value) => value !== undefined && value !== null ? value.toLocaleString('en-IN') : '0',
                    color: '#8884d8'
                  },
                  { 
                    dataKey: 'total_amount', 
                    label: 'Amount (₹ thousands)',
                    valueFormatter: (_, context) => {
                    const item = salesData.marketplace_breakdown?.[context.dataIndex];
                    return item?.total_amount
                        ? `₹ ${item.total_amount.toLocaleString('en-IN')}`
                        : '₹ 0';
                    },
                    color: '#82ca9d'
                  }
                ]}
                height={400}
                tooltip={{ trigger: 'item' }}
                slotProps={{
                  legend: {
                    hidden: false,
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'right' },
                  }
                }}
                margin={{ top: 20, right: 50, left: 50, bottom: 30 }}
              />
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Marketplace</TableCell>
                    <TableCell align="right">Units Sold</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right">% of Sales</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesData.marketplace_breakdown?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {item.marketplace}
                      </TableCell>
                      <TableCell align="right">{item.total_units?.toLocaleString('en-IN')}</TableCell>
                      <TableCell align="right">₹ {item.total_amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell align="right">{item.percentage?.toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                  {!salesData.marketplace_breakdown?.length && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        
        {/* Category Analysis Tab */}
        {tabValue === 2 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Category Analysis</Typography>
            <Box sx={{ height: 400, mb: 3, width: '100%' }}>
              <BarChart
                dataset={salesData.category_breakdown?.map(item => ({
                  category: item.category,
                  gross_amount: item.total_amount / 1000, // Gross sales
                  net_amount: item.net_amount / 1000, // Net sales (after returns)
                  returns: item.total_returns_amount / 1000, // Returns amount
                  total_units: item.total_units,
                  returns_units: item.total_returns_units
                })) || []}
                xAxis={[{ scaleType: 'band', dataKey: 'category', label: 'Category' }]}
                series={[
                  { 
                    dataKey: 'net_amount', 
                    label: 'Net Sales (₹ thousands)',
                    valueFormatter: (_, context) => {
                      const item = salesData.category_breakdown?.[context.dataIndex];
                      return item?.net_amount
                          ? `₹ ${item.net_amount.toLocaleString('en-IN')}`
                          : '₹ 0';
                    },
                    color: '#4caf50' // Green for net sales
                  },
                  { 
                    dataKey: 'returns', 
                    label: 'Returns (₹ thousands)',
                    valueFormatter: (_, context) => {
                      const item = salesData.category_breakdown?.[context.dataIndex];
                      return item?.total_returns_amount
                          ? `₹ ${item.total_returns_amount.toLocaleString('en-IN')}`
                          : '₹ 0';
                    },
                    color: '#f44336' // Red for returns
                  }
                ]}
                axisHighlight={{
                  x: 'line',
                  y: 'line'
                }}
                height={400}
                tooltip={{ trigger: 'item' }}
                slotProps={{
                  legend: {
                    hidden: false,
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'right' },
                  }
                }}
                margin={{ top: 20, right: 50, left: 50, bottom: 30 }}
              />
            </Box>
            
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Gross Sales</TableCell>
                    <TableCell align="right">Returns</TableCell>
                    <TableCell align="right">Net Sales</TableCell>
                    <TableCell align="right">Return Rate</TableCell>
                    <TableCell align="right">Units Sold</TableCell>
                    <TableCell align="right">Units Returned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesData.category_breakdown?.map((item, index) => (
                    <TableRow key={index} 
                      sx={{ 
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                        bgcolor: item.return_rate > 10 ? 'rgba(255, 0, 0, 0.02)' : 'inherit'
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {item.category}
                      </TableCell>
                      <TableCell align="right">₹ {item.total_amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell align="right" sx={{ color: item.total_returns_amount > 0 ? 'error.main' : 'inherit' }}>
                        ₹ {item.total_returns_amount?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                        ₹ {item.net_amount?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        color: item.return_rate > 10 ? 'error.main' : 
                               item.return_rate > 5 ? 'warning.main' : 'inherit' 
                      }}>
                        {item.return_rate?.toFixed(2)}%
                      </TableCell>
                      <TableCell align="right">{item.total_units?.toLocaleString('en-IN')}</TableCell>
                      <TableCell align="right">{item.total_returns_units?.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                  {!salesData.category_breakdown?.length && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        
        {/* Top Products Tab */}
        {tabValue === 3 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Top 10 Products by Net Sales</Typography>
            
            <Box sx={{ height: 400, mb: 3, width: '100%' }}>
              <BarChart
                dataset={salesData.top_products?.map(item => ({
                  product_name: item.product_name,
                  product_id: item.product_id,
                  gross_sales: item.total_amount / 1000,
                  net_sales: item.net_amount / 1000,
                  returns: item.total_returns_amount / 1000
                })) || []}
                layout="horizontal"
                yAxis={[{ scaleType: 'band', dataKey: 'product_name', label: 'Product' }]}
                xAxis={[{ scaleType: 'linear', label: 'Amount (₹ thousands)' }]}
                series={[
                  { 
                    dataKey: 'net_sales', 
                    label: 'Net Sales',
                    valueFormatter: (_, context) => {
                      const item = salesData.top_products?.[context.dataIndex];
                      return item?.net_amount
                          ? `₹ ${item.net_amount.toLocaleString('en-IN')}`
                          : '₹ 0';
                    },
                    color: '#4caf50'  // Green for net sales
                  },
                  { 
                    dataKey: 'returns', 
                    label: 'Returns',
                    valueFormatter: (_, context) => {
                      const item = salesData.top_products?.[context.dataIndex];
                      return item?.total_returns_amount
                          ? `₹ ${item.total_returns_amount.toLocaleString('en-IN')}`
                          : '₹ 0';
                    },
                    color: '#f44336'  // Red for returns
                  }
                ]}
                axisHighlight={{
                  x: 'line',
                  y: 'line'
                }}
                height={400}
                tooltip={{ trigger: 'item' }}
                slotProps={{
                  legend: {
                    hidden: false,
                    direction: 'row',
                    position: { vertical: 'top', horizontal: 'right' },
                  }
                }}
                margin={{ top: 20, right: 30, left: 150, bottom: 30 }}
              />
            </Box>
            
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Gross Sales</TableCell>
                    <TableCell align="right">Returns</TableCell>
                    <TableCell align="right">Net Sales</TableCell>
                    <TableCell align="right">Return Rate</TableCell>
                    <TableCell align="right">Units Sold</TableCell>
                    <TableCell align="right">Units Returned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesData.top_products?.map((item, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                        bgcolor: item.return_rate > 10 ? 'rgba(255, 0, 0, 0.02)' : 'inherit'
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {item.product_name}
                      </TableCell>
                      <TableCell align="right">₹ {item.total_amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell align="right" sx={{ color: item.total_returns_amount > 0 ? 'error.main' : 'inherit' }}>
                        ₹ {item.total_returns_amount?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                        ₹ {item.net_amount?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right" sx={{ 
                        color: item.return_rate > 10 ? 'error.main' : 
                               item.return_rate > 5 ? 'warning.main' : 'inherit' 
                      }}>
                        {item.return_rate?.toFixed(2)}%
                      </TableCell>
                      <TableCell align="right">{item.total_units?.toLocaleString('en-IN')}</TableCell>
                      <TableCell align="right">{item.total_returns_units?.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                  {!salesData.top_products?.length && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" color="primary">Download Report</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesBreakdown;
